import { useCallback, useEffect, useState } from 'react';
import { api } from '@halo/web-lib';
import { AlertTriangle, Info, Loader2, Power, Wrench } from 'lucide-react';

interface MaintenanceState {
  enabled: boolean;
  message: string | null;
  enabledAt: string | null;
}

const DISABLED: MaintenanceState = { enabled: false, message: null, enabledAt: null };

export default function MaintenancePage() {
  const [state, setState] = useState<MaintenanceState>(DISABLED);
  const [draftMessage, setDraftMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<MaintenanceState>('/api/v1/admin/maintenance');
      if (res.success && res.data) {
        setState(res.data);
        setDraftMessage(res.data.message ?? '');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<MaintenanceState>('/api/v1/admin/maintenance', {
        message: draftMessage.trim() || undefined,
      });
      if (res.success && res.data) setState(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.delete<MaintenanceState>('/api/v1/admin/maintenance');
      if (res.success && res.data) {
        setState(res.data);
        setDraftMessage('');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        <Power className="w-5 h-5 text-gray-700 dark:text-gray-200" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Maintenance Mode</h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Pull the plug on the user dashboard. Admins continue to use the app and can test fixes.
          </p>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 text-amber-800 dark:text-amber-300 text-xs">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>UI-only guard — the backend does not block requests. Non-admin users see a maintenance screen.</span>
      </div>

      <div
        className={`bg-white dark:bg-gray-900 rounded-xl border ${
          state.enabled
            ? 'border-amber-300 dark:border-amber-900/60'
            : 'border-gray-200 dark:border-gray-800'
        } p-5 mb-4`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center h-10 w-10 rounded-full ${
              state.enabled
                ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
                : 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300'
            }`}
          >
            <Wrench className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {state.enabled ? 'Maintenance mode is ON' : 'Maintenance mode is OFF'}
            </div>
            {state.enabled && state.enabledAt ? (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Enabled {new Date(state.enabledAt).toLocaleString()}
              </div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Users have normal access to the dashboard.
              </div>
            )}
          </div>
          {state.enabled ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Live
            </span>
          ) : null}
        </div>
        {state.enabled && state.message ? (
          <p className="mt-3 pl-13 text-sm text-gray-700 dark:text-gray-200 italic">
            &ldquo;{state.message}&rdquo;
          </p>
        ) : null}
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          Message shown to users
        </label>
        <textarea
          value={draftMessage}
          onChange={(e) => setDraftMessage(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="We'll be back shortly — performing scheduled upgrades."
          className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition"
        />
        <div className="flex justify-between items-center mt-1">
          <span className="text-[10px] text-gray-400">Optional — leave blank for the default message.</span>
          <span className="text-[10px] text-gray-400">{draftMessage.length}/500</span>
        </div>

        <div className="flex gap-2 mt-4">
          {state.enabled ? (
            <>
              <button
                type="button"
                onClick={disable}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                Disable maintenance
              </button>
              <button
                type="button"
                onClick={enable}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 transition"
              >
                Update message
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={enable}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 transition"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              Enable maintenance
            </button>
          )}
        </div>

        {error ? (
          <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/60 text-xs text-red-700 dark:text-red-400">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
